import { CONSENT_COOKIE, CONSENT_MAX_AGE_DAYS, type ConsentChoice } from "./shared.ts";

/**
 * Browser side of analytics: the consent cookie, gtag.js (GA4 with Consent Mode v2) and the Meta Pixel. Only used by
 * src/components/TrackingProvider.tsx, which decides on which pages anything loads.
 */

type Gtag = (...args: unknown[]) => void;
interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export function readConsent(): ConsentChoice | null {
  const m = new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=(granted|denied)`).exec(document.cookie);
  return m ? (m[1] as ConsentChoice) : null;
}

export function writeConsent(choice: ConsentChoice): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${choice}; Max-Age=${CONSENT_MAX_AGE_DAYS * 86400}; Path=/; SameSite=Lax${secure}`;
}

function addScript(src: string): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

const consentFields = (granted: boolean) => {
  const v = granted ? "granted" : "denied";
  return { ad_storage: v, ad_user_data: v, ad_personalization: v, analytics_storage: v };
};

/**
 * Installs gtag.js once. Consent Mode v2 starts from the guest's choice: until they accept, GA only receives cookieless
 * pings (Google models the gaps) and nothing is stored on their device.
 */
export function ensureGtag(firstId: string, granted: boolean): Gtag {
  if (window.gtag) return window.gtag;
  window.dataLayer = window.dataLayer || [];
  const gtag: Gtag = function gtag() {
    // gtag.js reads the `arguments` object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;
  gtag("consent", "default", consentFields(granted));
  gtag("set", "ads_data_redaction", !granted);
  gtag("js", new Date());
  addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(firstId)}`);
  return gtag;
}

export function updateGtagConsent(granted: boolean): void {
  if (!window.gtag) return;
  window.gtag("consent", "update", consentFields(granted));
  window.gtag("set", "ads_data_redaction", !granted);
}

/** Meta's loader, typed: a queue that fbevents.js drains once it has loaded. Only called after consent. */
export function ensureFbq(): Fbq {
  if (window.fbq) return window.fbq;
  const n = function fbq(...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  } as Fbq;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  addScript("https://connect.facebook.net/en_US/fbevents.js");
  return n;
}

// ---------------------------------------------------------------------------------------------
// Campaign parameters
// ---------------------------------------------------------------------------------------------

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const UTM_STORE = "vos_utm";
export type Utm = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/** Keeps the campaign the guest arrived with for this visit, so a booking made a few pages later still carries it. */
export function captureUtm(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Utm = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k)?.trim();
      if (v) utm[k] = v.slice(0, 100);
    }
    if (Object.keys(utm).length) sessionStorage.setItem(UTM_STORE, JSON.stringify(utm));
  } catch {
    // Storage blocked: bookings just go without campaign data.
  }
}

export function storedUtm(): Utm {
  try {
    const raw = JSON.parse(sessionStorage.getItem(UTM_STORE) || "{}") as Record<string, unknown>;
    const out: Utm = {};
    for (const k of UTM_KEYS) if (typeof raw[k] === "string") out[k] = raw[k] as string;
    return out;
  } catch {
    return {};
  }
}

/** True the first time for a key in this browser; used so a reloaded payment page doesn't count a purchase twice. */
export function firstTimeInBrowser(key: string): boolean {
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}
